import { claims } from '@manifoldxyz/claim-contracts';
import { StringUtils } from '@manifoldxyz/js-ts-utils';
import { appsApi } from '@/api/apps';
import {
  DEFAULT_STEP_STATE,
  DELEGATION_REGISTRY_ADDRESS_V1,
  DELEGATION_REGISTRY_ADDRESS_V2,
} from '@/common/constants';
import { TELEMETRY_EVENT_MAP } from '@/common/telemetry';
import {
  StepConfig,
  TransactionStep,
  TransactionStepState,
  TxStepId,
  TxStepStatus,
} from '@/common/types';
import { getExtensionAddress } from '@/common/util';
import DelegationRegistryContract, {
  DelegationType,
  IDelegate,
} from '@/contracts/delegationRegistryContract';
import useMainStore, { StoreType } from '@/store/mainStore';

export function createDelegateWalletStep(overrides?: {
  config?: Partial<StepConfig>;
  state?: Partial<TransactionStepState>;
}): TransactionStep {
  return {
    config: {
      id: TxStepId.SelectDelegateWallet,
      name: 'Select Delegate Wallet',
      SecondaryComponent: () => {
        const delegateAddresses = useMainStore((state) => state.delegateAddresses) || [];
        const selectedDelegateAddress = useMainStore((state) => state.selectedDelegateAddress);
        const setSelectedDelegateAddress = useMainStore(
          (state) => state.setSelectedDelegateAddress,
        );
        const walletAddress = useMainStore((state) => state.walletAddress);

        return (
          // for delegate wallet step show additional delegate wallet dropdown select
          <div className="m-claim--relative">
            <select
              id="delegate-wallet-select"
              aria-label="Select a delegate wallet"
              value={selectedDelegateAddress}
              onChange={(e) => setSelectedDelegateAddress(e.target.value)}
              className="m-claim--w-full m-claim--appearance-none m-claim--rounded-md m-claim--border m-claim--border-default m-claim--bg-surface-primary m-claim--px-3 m-claim--py-2 m-claim--pr-8 m-claim--text-sm m-claim--text-primary m-claim--outline-none"
            >
              {delegateAddresses.map((address) => (
                <option
                  key={address}
                  value={address}
                >
                  {StringUtils.abbreviateAddress(address)}
                </option>
              ))}
              <option
                key={'blank'}
                value={walletAddress}
              >
                {StringUtils.abbreviateAddress(walletAddress ?? '')} (Connected)
              </option>
            </select>
            <div className="m-claim--pointer-events-none m-claim--absolute m-claim--inset-y-0 m-claim--right-0 m-claim--flex m-claim--items-center m-claim--px-2">
              <svg
                className="m-claim--size-4 m-claim--text-secondary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>
        );
      },
      ...overrides?.config,
    },
    state: {
      ...DEFAULT_STEP_STATE,
      descriptionByStatus: {
        [TxStepStatus.Unstarted]: 'Select the wallet you want to use',
        [TxStepStatus.Pending]:
          'Your wallet is a delegate for other wallets. Select which one to use for the allowlist check',
        [TxStepStatus.Confirmed]: '', // @dev: filled out dynamically later to show the wallet address
        [TxStepStatus.Failed]: 'Failed to select allowlist wallet',
        [TxStepStatus.Confirming]: 'Confirming...',
      },
      ...overrides?.state,
    },
    validateBefore: async (store: StoreType) => {
      const studioInstance = useMainStore.getState().studioInstance;
      const connectedWalletAddress = useMainStore.getState().walletAddress;
      const analytics = useMainStore.getState().analytics;
      if (!studioInstance || !studioInstance.publicData.network || !connectedWalletAddress) {
        return;
      }

      // Only set delegates for gated Allowlist mints, they are not useful for other types of mints
      if (studioInstance.publicData.mintingRestriction !== 'Allowlist') {
        return;
      }

      const extensionAddressValue = getExtensionAddress(studioInstance);
      const extension = claims.getExtension(extensionAddressValue);

      const hasDelegatesV1 = claims.hasTrait(
        extension as claims.ClaimDeployment,
        claims.Trait.DelegateV1,
      );
      // @dev: fix this
      const hasDelegatesV2 = claims.hasTrait(
        extension as claims.ClaimDeployment,
        claims.Trait.DelegateV2,
      );

      const filteredDelegations: IDelegate[] = [];
      const potentiallyMisconfiguredDelegations: IDelegate[] = [];

      if (hasDelegatesV1 || hasDelegatesV2) {
        const delegationRegistryContract = new DelegationRegistryContract(
          studioInstance.publicData.network,
          DELEGATION_REGISTRY_ADDRESS_V1,
          hasDelegatesV1,
          DELEGATION_REGISTRY_ADDRESS_V2,
          hasDelegatesV2,
        );

        // Get delegations from delegate.cash using the Manifold Bridge Provider
        const delegations =
          await delegationRegistryContract.getDelegationsByDelegate(connectedWalletAddress);

        // Claim extensions only accepts wallet- and (claim extension contract)-scoped delegations, so filter out the rest.
        // If we detect delegations for a wallet in the claim, but they're incorrectly scoped, warn the user they may be misconfigured.
        for (const delegation of delegations) {
          if (delegation.type_ === DelegationType.ALL) {
            filteredDelegations.push(delegation);
          } else if (delegation.type_ === DelegationType.CONTRACT) {
            if (extensionAddressValue && delegation.contract_ === extensionAddressValue) {
              filteredDelegations.push(delegation);
            } else {
              potentiallyMisconfiguredDelegations.push(delegation);
            }
          } else {
            potentiallyMisconfiguredDelegations.push(delegation);
          }
        }
      }

      // dedupe
      const filteredDelegateAddresses = [
        ...new Set(filteredDelegations.map((delegation) => delegation.vault)),
      ];

      const finalDelegateAddresses: string[] = [];
      for (const delegateAddress of filteredDelegateAddresses) {
        // now check if each one is on the allowlist by requesting the merkle root from the claim extension
        const merkleInfo = await appsApi.getMerkleTree(
          studioInstance.publicData.instanceAllowlist?.merkleTreeId as number,
          delegateAddress,
        );

        // means the wallet is on the allowlist
        if (merkleInfo.length > 0) {
          finalDelegateAddresses.push(delegateAddress);
        }
      }

      if (finalDelegateAddresses.length > 0 && analytics) {
        analytics?.capture({
          event: TELEMETRY_EVENT_MAP.USES_DELEGATE_WALLET,
        });
      }

      store.setDelegateAddresses(finalDelegateAddresses);
      if (!finalDelegateAddresses?.length) {
        return {
          skip: true,
        };
      }
    },

    execute: async (
      _: StoreType,
      updateStepState: (updates: Partial<TransactionStepState>) => void,
    ) => {
      const latestTxSteps = useMainStore.getState().txSteps;
      const delegateStepIndex = latestTxSteps.findIndex(
        (step) => step.config.id === TxStepId.SelectDelegateWallet,
      );
      const latestSelectedDelegateAddress = useMainStore.getState().selectedDelegateAddress;

      // show the delegate wallet in the confirmed status description
      // show the delegate wallet in the confirmed status description
      updateStepState({
        descriptionByStatus: {
          ...latestTxSteps[delegateStepIndex].state.descriptionByStatus,
          [TxStepStatus.Confirmed]: `Delegate wallet selected: ${StringUtils.abbreviateAddress(latestSelectedDelegateAddress)}`,
        },
      });
    },
  };
}
